import React from 'react';

import { parseAgePricingFromItem } from './AgePricingEditor';
import './css/AgePricingDisplay.css';

function formatMoney(price, currency = 'INR') {
    if (price === '' || price == null) return '—';
    const num = Number.parseFloat(price);
    if (!Number.isFinite(num)) return String(price);
    const symbol = String(currency).toUpperCase() === 'USD' ? '$' : '₹';
    return `${symbol}${num}`;
}

function formatQty(value) {
    if (!hasDisplayValue(value)) return '—';
    return String(value).trim();
}

function hasDisplayValue(value) {
    return value !== '' && value != null && String(value).trim() !== '';
}

export function resolveItemAgePricing(item) {
    return parseAgePricingFromItem(item);
}

export function hasAgePricing(item) {
    return resolveItemAgePricing(item).length > 0;
}

export function formatAgeGroupLabel(tier) {
    const min = tier?.minAge ?? tier?.Min_Age;
    const max = tier?.maxAge ?? tier?.Max_Age;
    if (min === '' || min == null || max === '' || max == null) return '—';
    return `${min}–${max} yrs`;
}

function formatBaseLabel(hasAgeBands) {
    return hasAgeBands ? 'Other ages' : 'All ages';
}

function AgePricingRow({ label, value }) {
    return (
        <li className="age-pricing-list__row">
            <span className="age-pricing-list__label">{label}</span>
            <span className="age-pricing-list__value">{value}</span>
        </li>
    );
}

function AgePricingComboParts({ label, quantity, price }) {
    return (
        <>
            <span className="age-pricing-combo__age">{label}</span>
            <span className="age-pricing-combo__divider" aria-hidden="true" />
            <span className="age-pricing-combo__qty">{quantity}</span>
            <span className="age-pricing-combo__divider" aria-hidden="true" />
            <span className="age-pricing-combo__price">{price}</span>
        </>
    );
}

function AgePricingComboHeader() {
    return (
        <li
            className="age-pricing-list__row age-pricing-list__row--combo age-pricing-list__row--header"
            aria-hidden="true"
        >
            <span className="age-pricing-combo__age">Age</span>
            <span className="age-pricing-combo__divider" />
            <span className="age-pricing-combo__qty">Quantity</span>
            <span className="age-pricing-combo__divider" />
            <span className="age-pricing-combo__price">Price</span>
        </li>
    );
}

/**
 * One row per age band: age | quantity | price
 */
export function AgePricingComboCell({
    item,
    fallbackPrice,
    fallbackQuantity,
    currency = 'INR',
}) {
    const tiers = resolveItemAgePricing(item);
    const hasAgeBands = tiers.length > 0;
    const showBase =
        hasDisplayValue(fallbackPrice) || hasDisplayValue(fallbackQuantity);

    if (!showBase && !hasAgeBands) {
        return <span className="age-pricing-list__empty">—</span>;
    }

    return (
        <ul className="age-pricing-list age-pricing-list--combo" aria-label="Age pricing">
            {showBase ? (
                <li className="age-pricing-list__row age-pricing-list__row--combo">
                    <AgePricingComboParts
                        label={formatBaseLabel(hasAgeBands)}
                        quantity={formatQty(fallbackQuantity)}
                        price={formatMoney(fallbackPrice, currency)}
                    />
                </li>
            ) : null}
            {tiers.map((tier, index) => (
                <li
                    key={tier.agePricingId ?? `tier-${tier.minAge}-${tier.maxAge}-${index}`}
                    className="age-pricing-list__row age-pricing-list__row--combo"
                >
                    <AgePricingComboParts
                        label={formatAgeGroupLabel(tier)}
                        quantity={formatQty(tier.quantity)}
                        price={formatMoney(tier.price, currency)}
                    />
                </li>
            ))}
        </ul>
    );
}

/**
 * Edit prices; quantity is shown read-only in the same row.
 * Row format: age | quantity | ₹ [price]
 * onChange receives { basePrice, agePricing }.
 */
export function AgePricingComboEditor({
    item,
    basePrice,
    baseQuantity,
    agePricing,
    onChange,
    currency = 'INR',
    disabled = false,
}) {
    const tiers = Array.isArray(agePricing)
        ? agePricing
        : resolveItemAgePricing(item);
    const hasAgeBands = tiers.length > 0;
    const baseLabel = formatBaseLabel(hasAgeBands);
    const symbol = String(currency).toUpperCase() === 'USD' ? '$' : '₹';
    const resolvedBaseQty =
        baseQuantity ??
        item?.Quantity ??
        item?.quantity ??
        '';

    const emit = (nextBase, nextTiers) => {
        onChange?.({
            basePrice: nextBase,
            agePricing: nextTiers,
        });
    };

    const updateTierPrice = (index, price) => {
        const nextTiers = tiers.map((tier, i) =>
            i === index ? { ...tier, price } : tier
        );
        emit(basePrice ?? '', nextTiers);
    };

    return (
        <ul
            className="age-pricing-list age-pricing-list--combo age-pricing-list--edit"
            aria-label="Edit age-based prices"
        >
            <AgePricingComboHeader />
            <li className="age-pricing-list__row age-pricing-list__row--combo">
                <span className="age-pricing-combo__age">{baseLabel}</span>
                <span className="age-pricing-combo__divider" aria-hidden="true" />
                <span className="age-pricing-combo__qty">{formatQty(resolvedBaseQty)}</span>
                <span className="age-pricing-combo__divider" aria-hidden="true" />
                <span className="age-pricing-combo__price age-pricing-list__value--edit">
                    <span className="age-pricing-list__currency" aria-hidden="true">
                        {symbol}
                    </span>
                    <input
                        type="number"
                        className="age-pricing-list__input"
                        value={basePrice ?? ''}
                        disabled={disabled}
                        min="0"
                        step="0.01"
                        onChange={(e) => emit(e.target.value, tiers)}
                        aria-label={`${baseLabel} price`}
                    />
                </span>
            </li>
            {tiers.map((tier, index) => (
                <li
                    key={tier.agePricingId ?? `tier-${tier.minAge}-${tier.maxAge}-${index}`}
                    className="age-pricing-list__row age-pricing-list__row--combo"
                >
                    <span className="age-pricing-combo__age">{formatAgeGroupLabel(tier)}</span>
                    <span className="age-pricing-combo__divider" aria-hidden="true" />
                    <span className="age-pricing-combo__qty">{formatQty(tier.quantity)}</span>
                    <span className="age-pricing-combo__divider" aria-hidden="true" />
                    <span className="age-pricing-combo__price age-pricing-list__value--edit">
                        <span className="age-pricing-list__currency" aria-hidden="true">
                            {symbol}
                        </span>
                        <input
                            type="number"
                            className="age-pricing-list__input"
                            value={tier.price ?? ''}
                            disabled={disabled}
                            min="0"
                            step="0.01"
                            onChange={(e) => updateTierPrice(index, e.target.value)}
                            aria-label={`${formatAgeGroupLabel(tier)} price`}
                        />
                    </span>
                </li>
            ))}
        </ul>
    );
}

/**
 * Edit base + each age-band price (price-only list).
 * Prefer AgePricingComboEditor when quantity is available.
 */
export function AgePricingPriceEditor({
    item,
    basePrice,
    agePricing,
    onChange,
    currency = 'INR',
    disabled = false,
    baseQuantity,
}) {
    return (
        <AgePricingComboEditor
            item={item}
            basePrice={basePrice}
            baseQuantity={baseQuantity}
            agePricing={agePricing}
            onChange={onChange}
            currency={currency}
            disabled={disabled}
        />
    );
}

function AgePricingCellContent({ tiers, valueKey, fallbackValue, currency = 'INR' }) {
    const showBase = hasDisplayValue(fallbackValue);
    const ageTiers = Array.isArray(tiers) ? tiers : [];
    const hasAgeBands = ageTiers.length > 0;

    if (!showBase && !hasAgeBands) {
        return <span className="age-pricing-list__empty">—</span>;
    }

    const baseValue =
        valueKey === 'price'
            ? formatMoney(fallbackValue, currency)
            : String(fallbackValue).trim();

    return (
        <ul className="age-pricing-list" aria-label="Age-based portions">
            {showBase ? (
                <AgePricingRow
                    label={formatBaseLabel(hasAgeBands)}
                    value={baseValue}
                />
            ) : null}
            {ageTiers.map((tier, index) => {
                const value =
                    valueKey === 'price'
                        ? formatMoney(tier.price, currency)
                        : tier.quantity
                          ? String(tier.quantity).trim()
                          : '—';
                return (
                    <AgePricingRow
                        key={
                            tier.agePricingId ??
                            `tier-${tier.minAge}-${tier.maxAge}-${index}`
                        }
                        label={formatAgeGroupLabel(tier)}
                        value={value}
                    />
                );
            })}
        </ul>
    );
}

export function AgePricingPriceCell({ item, fallbackPrice, currency = 'INR' }) {
    return (
        <AgePricingCellContent
            tiers={resolveItemAgePricing(item)}
            valueKey="price"
            fallbackValue={fallbackPrice}
            currency={currency}
        />
    );
}

export function AgePricingQuantityCell({ item, fallbackQuantity }) {
    return (
        <AgePricingCellContent
            tiers={resolveItemAgePricing(item)}
            valueKey="quantity"
            fallbackValue={fallbackQuantity}
        />
    );
}

export function AgePricingReadOnlyPanel({ item, currency = 'INR', fallbackPrice, fallbackQuantity }) {
    const tiers = resolveItemAgePricing(item);
    const showBasePrice = hasDisplayValue(fallbackPrice);
    const showBaseQuantity = hasDisplayValue(fallbackQuantity);

    if (tiers.length === 0 && !showBasePrice && !showBaseQuantity) {
        return (
            <p className="age-pricing-readonly__empty">
                No menu price, quantity, or age-based portions configured.
            </p>
        );
    }

    return (
        <div className="age-pricing-readonly">
            <p className="age-pricing-readonly__title">Age-based portions</p>
            <AgePricingComboCell
                item={item}
                fallbackPrice={fallbackPrice}
                fallbackQuantity={fallbackQuantity}
                currency={currency}
            />
        </div>
    );
}
